import { useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { languageFromPath, localizedPath } from "src/utils/languagePaths"

/* Keeps the URL and the language saying the same thing (`utils/languagePaths` has the tables).

   Two directions, and which one runs is decided by what changed:

   - **The path changed** and names a language: the path wins. Somebody opened `/es/how-to-play`
     from a search result or a shared link, and the page they were promised is the Spanish one —
     so the language is switched (and remembered, through the detector's cache) and the URL is
     left alone.
   - **Otherwise the language wins** and the URL follows it: the switcher was pressed, or the app
     opened at a bare public URL for a player whose setting is Spanish, and the address is
     replaced (no history entry) with the one that says what the page shows.

   Without the distinction the two chase each other: landing on `/es/x` with English still
   loaded would read as "language is English, move the URL to `/x`" before the dictionary arrived.
   The ref is what tells a navigation from a language change.

   `<html lang>` is set here too. The server writes it for the crawler from the URL; this writes it
   for the screen reader from the language, once the player's own is known. */
const LanguageInPath = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const language = i18n.resolvedLanguage
  const lastPathname = useRef(null)

  useEffect(() => {
    const pathChanged = lastPathname.current !== pathname
    lastPathname.current = pathname
    const inPath = languageFromPath(pathname)
    if (pathChanged && inPath && inPath !== language) {
      i18n.changeLanguage(inPath)
      return
    }
    const wanted = localizedPath(pathname, language)
    if (wanted !== pathname) navigate(wanted, { replace: true })
  }, [pathname, language, i18n, navigate])

  useEffect(() => {
    if (language) document.documentElement.lang = language
  }, [language])

  return null
}

export default LanguageInPath
